import { createRouter, createWebHistory } from "@ionic/vue-router";
import { RouteRecordRaw } from "vue-router";
import adminRoutes from "@/router/routes/adminRoutes";
import advisorRoutes from "@/router/routes/advisorRoutes";
import {
  ROUTE_ADVISOR_CHANGE_PASSWORD,
  ROUTE_ADVISOR_RESET_PASSWORD,
  ROUTE_ADVISOR_SIGN_IN,
  ROUTE_ADVISOR_SIGN_IN_2FA,
  ROUTE_CHANGE_PASSWORD,
  ROUTE_CHANGE_PASSWORD_ADMIN,
  ROUTE_FINALISE_ACCOUNT,
  ROUTE_HOME_ADMIN,
  ROUTE_HOME_ADVISOR,
  ROUTE_HOME_INVESTORS,
  ROUTE_RESET_PASSWORD,
  ROUTE_RESET_PASSWORD_ADMIN,
  ROUTE_SIGN_IN,
  ROUTE_SIGN_IN_2FA,
  ROUTE_SIGN_IN_2FA_ADMIN,
  ROUTE_SIGN_IN_ADMIN,
} from "@/constants/routes";
import { useAuthentication } from "@/composables/useAuthentication";
import authRoutes from "@/router/routes/authRoutes";
import investorRoutes from "@/router/routes/investorRoutes";
import errorRoutes from "@/router/routes/errorRoutes";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: ROUTE_SIGN_IN,
  },
  ...adminRoutes,
  ...advisorRoutes,
  ...authRoutes,
  ...investorRoutes,
  ...errorRoutes,
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, isAdmin, isAdvisor, restoreUser } = useAuthentication();
  const authRoutes = [
    ROUTE_SIGN_IN,
    ROUTE_SIGN_IN_ADMIN,
    ROUTE_ADVISOR_SIGN_IN,
    ROUTE_SIGN_IN_2FA,
    ROUTE_SIGN_IN_2FA_ADMIN,
    ROUTE_ADVISOR_SIGN_IN_2FA,
    ROUTE_RESET_PASSWORD,
    ROUTE_RESET_PASSWORD_ADMIN,
    ROUTE_ADVISOR_RESET_PASSWORD,
    ROUTE_CHANGE_PASSWORD,
    ROUTE_CHANGE_PASSWORD_ADMIN,
    ROUTE_ADVISOR_CHANGE_PASSWORD,
    ROUTE_FINALISE_ACCOUNT,
  ];

  if (!authRoutes.includes(to.path) && to.meta.admin) await restoreUser(true);
  if (!authRoutes.includes(to.path) && to.meta.advisor) await restoreUser(false, true);

  const resolveSignInForTarget = (): string => {
    if (to.meta.advisor) return ROUTE_ADVISOR_SIGN_IN;
    if (to.meta.admin) return ROUTE_SIGN_IN_ADMIN;
    return ROUTE_SIGN_IN;
  };

  const resolveHomeForCurrentRole = (): string => {
    if (isAdvisor.value) return ROUTE_HOME_ADVISOR;
    if (isAdmin.value) return ROUTE_HOME_ADMIN;
    return ROUTE_HOME_INVESTORS;
  };

  /**
   * Route is protected when we don't have a token.
   * User should sign in before proceeding.
   */
  if (!authRoutes.includes(to.path) && !isAuthenticated.value) return next({ path: resolveSignInForTarget() });

  /**
   * Routes are protected per role. When a user lands on a route
   * outside of their environment, send them to their own home.
   */
  if (to.meta.advisor && !isAdvisor.value) return next({ path: resolveHomeForCurrentRole() });
  if (to.meta.admin && !isAdmin.value) return next({ path: resolveHomeForCurrentRole() });
  if (to.meta.investor && (isAdmin.value || isAdvisor.value)) return next({ path: resolveHomeForCurrentRole() });

  /**
   * User is already signed in and trying to navigate to a sign-in / auth
   * route. Send them to their role-specific home page.
   */
  if (authRoutes.includes(to.path) && isAuthenticated.value) return next({ path: resolveHomeForCurrentRole() });

  next();
});

export default router;
